import classNames from 'classnames'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ROUTE_SUGGEST } from '../../routes'
import { navigateTo } from '../../store/history/history.actions'
import { selectCurrentHistoryRoute, selectIsHomePage } from '../../store/history/history.selectors'
import { selectHasSearchIconFromRouteOptions } from '../../store/poiassets/poiassets.selectors'
import { setSearchTerms } from '../../store/search/searchSlice'
import { suggest } from '../../store/suggest/suggest.actions'
import { UI_ELEMENTS } from '../../store/ui/ui.constants'
import { isGE } from '../Device'
import Throbber from '../loading/Throbber'
import { isPagelessRoute } from '../router/routeUtils'
import SearchInputIcon from '../search/SearchInputIcon'
import useUIDisplay from '../ui/useUIDisplay'
import { useAction } from '../utils/react-redux'
import useDebounce from '../utils/useDebounce'
import { usePrevious } from '../utils/usePrevious'
import QuickSearchInput from './QuickSearchInput'
import s from './SearchInput.less'

const SearchInput = forwardRef((props, ref) => {
  const {
    value,
    loading,
    onChange,
    onFocus,
    suggestFilterType,
    className,
    placeholder,
    idx = 0,
    prefix,
    hasGreenPlaceholder = false,
    navigateToSuggestOnFocus = true,
    isFirstStepToFill = true,
    transparent
  } = props

  const dispatch = useDispatch()
  const [hasFocus, setFocus] = useState(false)
  const route = useSelector(selectCurrentHistoryRoute)
  const hasIcon = useSelector(selectHasSearchIconFromRouteOptions)
  const prevRoute = usePrevious(route)
  const execSuggest = useAction(suggest)
  const execNavigateTo = useAction(navigateTo)
  const [isReady, setIsReady] = useState(false)
  const inputRef = useRef()
  const debouncedValue = useDebounce(value, 250)
  const hasDebouncedValueChangeOnce = useRef(false) // to ignore suggest on first render
  const isBtnOkDisplayed = isGE() && route === ROUTE_SUGGEST && !loading
  const isQuickSearch = isGE() && !hasFocus && (!value || value === '') && isFirstStepToFill
  const inputId = `SearchInput${idx}`
  const { showUI } = useUIDisplay(UI_ELEMENTS.SUGGEST)
  const isHomePage = useSelector(selectIsHomePage)

  const onSubmit = useCallback(() => {
    dispatch(setSearchTerms(value))
    return Promise.resolve().then(() => blur())
  }, [value])

  useImperativeHandle(ref, () => {
    return { onSubmit }
  })

  useEffect(() => {
    if (__BROWSER__) setIsReady(true)
  }, [__BROWSER__])

  useEffect(() => {
    if (route !== prevRoute && isPagelessRoute(prevRoute) && !isPagelessRoute(route)) {
      blur()
    }
  }, [route])

  const doSuggest = val => {
    dispatch(setSearchTerms(val))
    return execSuggest({ terms: val, suggestFilterType })
  }

  useEffect(() => {
    if (hasDebouncedValueChangeOnce.current && hasFocus) {
      doSuggest(debouncedValue)
    }
    hasDebouncedValueChangeOnce.current = true
  }, [hasFocus, debouncedValue])

  const handleChange = event => {
    onChange?.({ value: event.target.value })
  }

  const handleFocus = useCallback(
    e => {
      setTimeout(() => {
        if (value.length !== 0) e.target.select()
      }, 0)
      if (navigateToSuggestOnFocus) execNavigateTo({ route: ROUTE_SUGGEST })
      showUI()
      onFocus?.()
      setFocus(true)
    },
    [execNavigateTo, showUI, doSuggest, onFocus, setFocus]
  )

  const blur = useCallback(() => {
    inputRef?.current?.blur()
    setFocus(false)
  }, [inputRef, setFocus])

  return (
    <div
      className={classNames(
        isHomePage ? s.homePageContainer : s.container,
        s.container,
        hasIcon && s.withIcon,
        className,
        transparent && s.transparent
      )}
    >
      {prefix && (
        <label className={classNames(s.prefix)} htmlFor={inputId}>
          {prefix}
        </label>
      )}
      {hasIcon && <SearchInputIcon />}
      <input
        ref={inputRef}
        type="text"
        id={inputId}
        value={value}
        className={classNames(s.field, isHomePage && hasGreenPlaceholder ? s.greenPlaceholder : s.whitePlaceholder)}
        placeholder={placeholder}
        onFocus={handleFocus}
        onChange={handleChange}
        disabled={!isReady}
        autoComplete="off"
      />
      {loading && (
        <span className={s.throbber}>
          <Throbber />
        </span>
      )}
      {isBtnOkDisplayed && <button className={s.btnOk}>OK</button>}
      {isQuickSearch && <QuickSearchInput onChange={onChange} searchInputRef={inputRef} />}
    </div>
  )
})

SearchInput.displayName = 'SearchInput'
export default SearchInput
